import React, { useContext, useEffect, useRef } from 'react'
import { View } from 'react-native'
import { useDispatch, useSelector } from 'react-redux'
import { take } from 'lodash'
import { useNavigation } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import moment from 'moment'
import { Button, Icon, Text } from '@src/components/base'
import { StyleService, useStyleSheet } from '@src/style/service'
import {
  ehrSuggestedAvailabilitySelector,
  insurancePolicySelector,
  nextRecurringAppointmentSlotSelector,
  pendingEhrAppointmentsSelector,
  upcomingEhrAppointmentsSelector,
} from '@src/screens/NutritionistHub/models/nutritionistHub.selectors'
import { AppStackParamList } from '@navigation/types'
import { useNutritionistHubTracker } from '@screens/NutritionistHub/hooks.ts'
import { EhrAppointmentKind } from '@src/types'
import { useGetRequiredPolicyConsents } from '@src/screens/PolicyConsents/hooks/useGetRequiredPolicyConsents'
import { ScrollViewWithFade } from '@src/components/ScrollViewWithFade'
import { NavigatorContext, useSnack } from '@src/utils/navigatorContext'
import { CustomEventTypes } from '@src/config/analytics'
import { ErrorMessages } from '@src/config/logger'
import { SLOT_TAKEN_ERROR } from '../../constants'
import { ScheduleNextCallCard } from '../ScheduleNextCall/ScheduleNextCallCard'
import { EhrAppointmentCustomType } from '../../utils'
import { SuggestedCallCard } from './SuggestedCallCard'
import { CallCard } from './CallCard'

interface UpcomingAppointmentsProps {
  hideScheduleNextCall: boolean
}

export const UpcomingAppointments = ({ hideScheduleNextCall }: UpcomingAppointmentsProps) => {
  const styles = useStyleSheet(themedStyles)
  const navigation = useNavigation<StackNavigationProp<AppStackParamList>>()
  const dispatch = useDispatch()
  const showSnack = useSnack()
  const { enableLoader, disableLoader } = useContext(NavigatorContext)
  const track = useNutritionistHubTracker()
  const bookingRef = useRef(false)

  const upcomingAppointments = useSelector(upcomingEhrAppointmentsSelector)
  const suggestedAvailability = useSelector(ehrSuggestedAvailabilitySelector)
  const nextRecurringAppointmentSlot = useSelector(nextRecurringAppointmentSlotSelector)
  const insurancePolicy = useSelector(insurancePolicySelector)
  const pendingAppointments = useSelector(pendingEhrAppointmentsSelector)

  const canBookCall =
    insurancePolicy?.inReview || insurancePolicy?.hasUnusedCalls || pendingAppointments.length > 0

  useEffect(() => {
    dispatch({ type: 'nutritionistHub/fetchUpcomingEhrAppointments' })
    dispatch({ type: 'nutritionistHub/fetchPendingEhrAppointments' })
    dispatch({ type: 'nutritionistHub/fetchAppointmentRecurrence' })
    dispatch({ type: 'nutritionistHub/fetchNextRecurringAppointmentSlot' })
  }, [dispatch])

  useEffect(() => {
    dispatch({
      type: 'nutritionistHub/fetchEhrSuggestedAvailability',
    })
  }, [dispatch])

  useEffect(
    () => () => {
      if (bookingRef.current) {
        disableLoader()
        bookingRef.current = false
      }
    },
    [disableLoader],
  )

  const handleSeeAllPress = () => {
    track(CustomEventTypes.NutritionistHubSeeAllCallsTapped)
    navigation.navigate('AllVideoCalls')
  }

  const { getRequiredPolicyConsents, loading } = useGetRequiredPolicyConsents()

  const onSchedulePress = ({
    appointmentKind,
    meetingAt,
    nutritionistId,
  }: {
    appointmentKind: EhrAppointmentKind
    meetingAt: string
    nutritionistId: string
  }) => {
    track(CustomEventTypes.NutritionistHubSuggestedCallConfirmTapped)

    if (!canBookCall) {
      navigation.navigate('VideoCallCheckout', { suggestedCallFlow: true })
      return
    }

    if (bookingRef.current) {
      return
    }

    bookingRef.current = true
    enableLoader()

    dispatch({
      type: 'nutritionistHub/bookAppointment',
      payload: {
        appointmentKind,
        date: meetingAt,
        coachId: nutritionistId,
        pendingAppointmentId: pendingAppointments[0]?.id,
      },
      success: async () => {
        dispatch({ type: 'nutritionistHub/fetchUpcomingEhrAppointments' })
        dispatch({
          type: 'nutritionistHub/fetchEhrSuggestedAvailability',
        })
        dispatch({ type: 'nutritionistHub/fetchInsurancePolicy' })

        const requiredPolicies = await getRequiredPolicyConsents()
        if (requiredPolicies.length > 0) {
          navigation.navigate('PolicyConsent', { policy: requiredPolicies[0] })
        }

        showSnack(
          `Video call scheduled successfully!`,
          null,
          'success',
          'check',
          undefined,
          'redesigned',
        )
      },
      failure: (error: Error) => {
        const errorMessage = error.message || ErrorMessages.ServerError
        if (errorMessage.includes(SLOT_TAKEN_ERROR)) {
          navigation.navigate('CallScheduling', {
            appointmentKind,
            allowBackNavigation: true,
            errorMessage:
              'The suggested appointment time is no longer available. Please choose a different time slot',
          })
        } else {
          showSnack(errorMessage, null, 'error', 'x', undefined, 'redesigned')
        }
      },
      complete: () => {
        disableLoader()
        bookingRef.current = false
      },
    })
  }

  const onCallCardPress = (appointment: EhrAppointmentCustomType) => {
    navigation.navigate('VideoCallModal', { id: appointment.id })
  }

  return (
    <ScrollViewWithFade>
      <View style={styles.container}>
        <View style={styles.sectionHeader}>
          <View style={styles.header}>
            <Icon name="calendar-blank" />
            <View style={styles.headerText}>
              <Text type="regular" bold>
                Upcoming Calls
              </Text>
            </View>
          </View>
          <Button
            accessibilityLabel="seeAllCalls"
            type="outline"
            size="small"
            style={styles.seeAllButton}
            onPress={handleSeeAllPress}
          >
            See All
          </Button>
        </View>
        <View style={styles.callsContainer}>
          {take(upcomingAppointments, 5).map((appointment) => {
            const { id, title, meetingAt, lastState, providerName, kind, recurring } = appointment
            const inFuture = meetingAt && moment(meetingAt).isAfter(moment())
            return (
              <CallCard
                key={id}
                title={title}
                meetingAt={meetingAt}
                lastStateKind={lastState.kind}
                coachName={providerName}
                appointmentKind={kind}
                chargeKind={appointment.attendances[0].chargeKind}
                recurring={recurring}
                onPress={inFuture ? () => onCallCardPress(appointment) : undefined}
              />
            )
          })}
          {nextRecurringAppointmentSlot && (
            <CallCard
              meetingAt={nextRecurringAppointmentSlot.meetingAt}
              coachName={nextRecurringAppointmentSlot.coachName}
              appointmentKind={nextRecurringAppointmentSlot.appointmentKind}
              placeholder
              recurring
            />
          )}
          {suggestedAvailability && insurancePolicy && (
            <SuggestedCallCard
              label="Suggested"
              title={suggestedAvailability.title}
              meetingAt={suggestedAvailability.meetingAt}
              coachName={suggestedAvailability.providerName}
              appointmentKind={suggestedAvailability.appointmentKind}
              onSchedulePress={() => {
                onSchedulePress({
                  appointmentKind: suggestedAvailability.appointmentKind,
                  meetingAt: suggestedAvailability.meetingAt,
                  nutritionistId: suggestedAvailability.providerId,
                })
              }}
              schedulingDisabled={loading}
            />
          )}
        </View>
      </View>

      <View style={styles.divider} />
      {!hideScheduleNextCall && <ScheduleNextCallCard />}
    </ScrollViewWithFade>
  )
}

const themedStyles = StyleService.create({
  container: {
    flex: 1,
    marginBottom: 24,
  },
  sectionHeader: {
    marginTop: 24,
    marginBottom: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerText: {
    marginLeft: 8,
    lineHeight: 24,
    flexDirection: 'column',
  },
  seeAllButton: {
    alignSelf: 'center',
  },
  callsContainer: {
    rowGap: 24,
  },
  suggestedCallText: {
    color: 'theme.text.tertiary',
    marginTop: 12,
  },
  divider: {
    borderBottomWidth: 1,
    borderColor: 'theme.surface.base1',
  },
})
